import {
  BanIcon,
  CrossIcon,
  Filter,
  InformationCircle,
  Loading2,
  Localisation,
  Plus,
  SearchLoop,
  Star,
} from 'assets/icons/index.js';
import axios from 'axios';
import Button from 'components/button/index.js';
import Badge from 'components/badge/badge.js';
import useModal from 'components/modal/useModal.js';
import Pagination from 'components/pagination/pagination.js';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext.js';
import useWindowDimensions from 'hooks/useWindowDimensions.js';
import { useEffect, useMemo, useState, useRef } from 'react';
import ApiDatabase from 'server';
import TableVivier from './components/table.vivier.js';
import SubNavbar from '../../../../../components/SubNavbar/SubNavbar.js';
import StyledInput from '../../../../../components/styledInput/StyledInput';
import MediaQuery from 'react-responsive';
import DatePicker from 'react-datepicker';
import BottomSheet from '../../../../../components/bottomSheet/BottomSheet';

const OP_STYLE = { color: '#D97706', fontWeight: 600 };

function renderHighlightedSearch(searchStr) {
  if (!searchStr) return null;
  const hasAdvanced =
    / ET /.test(searchStr) || / OU /.test(searchStr) || / NON /.test(searchStr)
    || /"/.test(searchStr) || /[()]/.test(searchStr)
    || /\w\*(?:\s|$)/.test(searchStr) || /~\d/.test(searchStr);
  if (!hasAdvanced) return null;

  const spans = [];
  let pos = 0;
  const len = searchStr.length;
  let k = 0;

  while (pos < len) {
    if (searchStr[pos] === ' ') {
      let end = pos;
      while (end < len && searchStr[end] === ' ') end++;
      spans.push(<span key={k++}>{searchStr.substring(pos, end)}</span>);
      pos = end;
      continue;
    }

    if (searchStr[pos] === '(' || searchStr[pos] === ')') {
      spans.push(<span key={k++} style={OP_STYLE}>{searchStr[pos]}</span>);
      pos++;
      continue;
    }

    if (searchStr[pos] === '"') {
      const close = searchStr.indexOf('"', pos + 1);
      if (close !== -1) {
        let end = close + 1;
        const pm = searchStr.substring(end).match(/^~\d+/);
        spans.push(<span key={k++} style={OP_STYLE}>"</span>);
        if (close > pos + 1) {
          spans.push(<span key={k++}>{searchStr.substring(pos + 1, close)}</span>);
        }
        spans.push(<span key={k++} style={OP_STYLE}>"</span>);
        if (pm) {
          spans.push(<span key={k++} style={OP_STYLE}>{pm[0]}</span>);
          end = close + 1 + pm[0].length;
        }
        pos = end;
        continue;
      } else {
        spans.push(<span key={k++}>{searchStr[pos]}</span>);
        pos++;
        continue;
      }
    }

    const wm = searchStr.substring(pos).match(/^[^\s()"]+/);
    if (wm) {
      const word = wm[0];
      if (word === 'ET' || word === 'OU' || word === 'NON') {
        spans.push(<span key={k++} style={OP_STYLE}>{word}</span>);
      } else if (word.endsWith('*') && word.length > 1) {
        spans.push(<span key={k++}>{word.slice(0, -1)}</span>);
        spans.push(<span key={k++} style={OP_STYLE}>*</span>);
      } else {
        const tildeMatch = word.match(/^(.+)(~\d+)$/);
        if (tildeMatch) {
          spans.push(<span key={k++}>{tildeMatch[1]}</span>);
          spans.push(<span key={k++} style={OP_STYLE}>{tildeMatch[2]}</span>);
        } else {
          spans.push(<span key={k++}>{word}</span>);
        }
      }
      pos += word.length;
      continue;
    }

    spans.push(<span key={k++}>{searchStr[pos]}</span>);
    pos++;
  }

  return <>{spans}</>;
}

const FILTER_CONFIGS = {
  activity: {
    label: 'Activité',
    type: 'select',
    options: [
      { value: 'active_today', label: "Actif aujourd'hui" },
      { value: 'active_week', label: 'Actif cette semaine' },
      { value: 'no_activity_7', label: 'Aucune activité depuis 7 jours' },
      { value: 'no_activity_10', label: 'Aucune activité depuis 10 jours' },
      { value: 'no_activity_30', label: 'Aucune activité depuis 30 jours' },
      { value: 'no_activity_90', label: 'Aucune activité depuis 90 jours' },
    ],
  },
  source: {
    label: 'Source candidat',
    type: 'select',
    options: [
      { value: 'alerte_emploi', label: 'Alerte emploi' },
      { value: 'candidature_spontanee', label: 'Candidature spontanée' },
      { value: 'cooptation', label: 'Cooptation' },
      { value: 'reseaux_sociaux', label: 'Réseaux sociaux' },
      { value: 'site_web', label: 'Site web' },
    ],
  },
  department: {
    label: 'Département - Ville',
    type: 'selectMultipleCheckbox',
    placeholder: 'Sélectionner un département',
    options: [],
  },
  os: {
    label: 'OS',
    type: 'toggle',
    options: [
      { value: 'web', label: 'Web' },
      { value: 'app', label: 'App' },
    ],
  },
  rating: {
    label: 'Évaluation moyenne',
    type: 'rating',
  },
  vivier: {
    label: 'Vivier',
    type: 'selectMultipleCheckbox',
    placeholder: 'Sélectionner un vivier',
    options: [],
  },
  cv: {
    label: 'CV',
    type: 'toggle',
    options: [
      { value: 'exists', label: 'Existe' },
      { value: 'not_exists', label: "N'existe pas" },
    ],
    exclusive: true,
  },
  registrationDate: {
    label: "Date d'inscription",
    type: 'dateRange',
  },
  archived: {
    label: 'Archivé',
    type: 'toggle',
    options: [
      { value: true, label: 'Oui' },
      { value: false, label: 'Non' },
    ],
    exclusive: true,
  },
};

function BodyVivier() {
  const token = localStorage.getItem('xsrfToken');
  const { toggle } = useModal();
  const { width } = useWindowDimensions();
  const { setInfoVivier, usersVivier, setUsersVivier } = UseContext();
  const [search, setSearch] = useState('');
  const [optionSearch, setOptionSearch] = useState('');
  const [selectProfil, setSelectProfil] = useState(false);
  const [searchCandidatWaiting, setSearchCandidatWaiting] = useState(true);
  const [sortName, setSortName] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitElement, setLimitElement] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [countPerPage, setCountPerPage] = useState(0);
  const [countTotal, setCountTotal] = useState(0);
  const [result, setResult] = useState();
  const [listAddVivier, setAddVivier] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const buildDefaultDeptFilter = (depts) => {
    return [
      ...depts.map((d) => ({ value: d.toLowerCase(), label: d, checked: false })),
      { value: 'other', label: 'Aucun', checked: false },
    ];
  };
  const [defaultFilterValues, setDefaultFilterValues] = useState({
    department: [{ value: 'other', label: 'Aucun', checked: false }],
    os: { web: false, app: false },
  });
  const [filterValues, setFilterValues] = useState(defaultFilterValues);
  const [filterApplyTrigger, setFilterApplyTrigger] = useState(0);
  const [vivierOptions, setVivierOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const filterListRef = useRef(null);
  const searchHighlight = useMemo(() => renderHighlightedSearch(search), [search]);

  // États pour le BottomSheet mobile
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempSearch, setTempSearch] = useState('');
  const [tempOptionSearch, setTempOptionSearch] = useState('');
  const openFilterSheet = () => {
    setTempSearch(search);
    setTempOptionSearch(optionSearch);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSearch(tempSearch);
    setOptionSearch(tempOptionSearch);
    setIsFilterOpen(false);
  };


  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}vivier/vivierAll`)
      .then(({ data }) => {
        setVivierOptions(data.map((v) => ({ value: v._id, label: v.name })));
      })
      .catch(() => {});

    axios
      .get(`${process.env.REACT_APP_API_URL}registrationSource`)
      .then(({ data }) => {
        setSourceOptions(data.map((s) => ({ value: s._id, label: s.name })));
      })
      .catch(() => {});

    ApiDatabase.getCityDepartments(
      { token },
      (data) => {
        const opts = [
          ...data.map((d) => ({ value: d.toLowerCase(), label: d })),
          { value: 'other', label: 'Aucun' },
        ];
        setDepartmentOptions(opts);
        const deptDefaults = buildDefaultDeptFilter(data);
        setDefaultFilterValues((prev) => ({ ...prev, department: deptDefaults }));
        setFilterValues((prev) => ({ ...prev, department: deptDefaults }));
      },
      () => {}
    );
  }, []);

  const selectedDepartments = (filterValues.department || [])
    .filter((d) => d.checked && d.value !== 'other')
    .map((d) => d.value);

  useEffect(() => {
    if (selectedDepartments.length === 0) {
      setCityOptions([]);
      setFilterValues((prev) => ({ ...prev, city: [] }));
      return;
    }
    ApiDatabase.getCitiesByDepartments(
      { token, departments: JSON.stringify(selectedDepartments) },
      (data) => {
        const opts = data.map((c) => ({
          value: c.name,
          label: c.name,
          checked: false,
        }));
        setCityOptions(opts);
        setFilterValues((prev) => {
          const prevCities = prev.city || [];
          const validValues = new Set(opts.map((o) => o.value));
          const kept = prevCities.filter((c) => c.checked && validValues.has(c.value));
          return {
            ...prev,
            city: opts.map((o) => ({
              ...o,
              checked: kept.some((k) => k.value === o.value),
            })),
          };
        });
      },
      () => {}
    );
  }, [selectedDepartments.join(',')]);

  const resolvedFilterConfigs = {
    ...FILTER_CONFIGS,
    department: { ...FILTER_CONFIGS.department, options: departmentOptions },
    source: { ...FILTER_CONFIGS.source, options: sourceOptions },
    vivier: { ...FILTER_CONFIGS.vivier, options: vivierOptions },
  };


  const handleAddVivier = () => {
    setInfoVivier({ listUsers: listAddVivier });
    toggle('vivier_add');
  };

  const manageCandidatSearch = () => {
    setSearchCandidatWaiting(true);

    const { vivier: vivierList, department: deptList, city: cityList, ...otherFilterValues } = filterValues;
    const checkedVivierIds = (vivierList || [])
      .filter((v) => v.checked)
      .map((v) => v.value);
    const checkedDeptIds = (deptList || [])
      .filter((v) => v.checked)
      .map((v) => v.value);
    const checkedCityNames = (cityList || [])
      .filter((v) => v.checked)
      .map((v) => v.value);

    const rawFilterOptions = {
      ...otherFilterValues,
      ...(checkedVivierIds.length > 0 ? { vivier: checkedVivierIds } : {}),
      ...(checkedDeptIds.length > 0 ? { department: checkedDeptIds } : {}),
      ...(checkedCityNames.length > 0 ? { city: checkedCityNames } : {}),
    };

    const filterOptions = Object.fromEntries(
      Object.entries(rawFilterOptions).filter(([, val]) => {
        if (val === null || val === undefined) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'object') return Object.values(val).some(Boolean);
        return true;
      })
    );

    ApiDatabase.getVivierListUsers(
      {
        token,
        page: currentPage,
        limit: limitElement,
        search,
        optionSearch,
        profil: selectProfil,
        filterOptions: JSON.stringify(filterOptions),
      },
      (data) => {
        setTotalPages(data.countPages);
        setCountPerPage(limitElement);
        setCountTotal(data.count);
        setResult(data.users);
        setSearchCandidatWaiting(false);
      },
      (error) => {
        if (error?.name !== 'CanceledError') {
          console.error('An error occurred:', error);
          setSearchCandidatWaiting(false);
        }
      }
    );
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, optionSearch, filterApplyTrigger, limitElement, selectProfil]);

  let timerViverSearch = '';
  useEffect(() => {
    timerViverSearch = setTimeout(() => {
      manageCandidatSearch();
    }, 400);
    return () => clearTimeout(timerViverSearch);
  }, [
    search,
    optionSearch,
    sortName,
    currentPage,
    selectProfil,
    filterApplyTrigger,
    limitElement,
  ]);

  const activeFilterCount = Object.keys(filterValues).filter((k) => {
    if (k === 'city') return false;
    const val = filterValues[k];
    if (val === null || val === undefined) return false;
    if (k === 'department') {
      const deptActive = (val || []).some((item) => item.checked);
      const cityActive = (filterValues.city || []).some((item) => item.checked);
      return deptActive || cityActive;
    }
    if (Array.isArray(val)) return val.some((item) => item.checked);
    if (typeof val === 'object') return Object.values(val).some(Boolean);
    return true;
  }).length;

  const setFilterValue = (filterKey, value) => {
    setFilterValues((prev) => ({ ...prev, [filterKey]: value }));
  };

  const clearFilterValue = (filterKey) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
  };

  const resetAllFilters = () => {
    setFilterValues(defaultFilterValues);
    setExpandedFilter(null);
    setFilterApplyTrigger((prev) => prev + 1);
  };

  const applyAllFilters = () => {
    setIsFilterPanelOpen(false);
    setExpandedFilter(null);
    setFilterApplyTrigger((prev) => prev + 1);
  };

  // Click outside to close expanded filter
  useEffect(() => {
    if (!expandedFilter) return;
    const handleClickOutside = (e) => {
      if (filterListRef.current && !filterListRef.current.contains(e.target)) {
        setExpandedFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedFilter]);

  // Get display value for a filter
  const getFilterDisplayValue = (filterKey) => {
    const value = filterValues[filterKey];
    const config = resolvedFilterConfigs[filterKey];
    if (value === null || value === undefined || !config) return null;

    if (config.type === 'toggle' && config.exclusive) {
      const opt = config.options.find((o) => o.value === value);
      return opt?.label || value;
    }
    if (config.type === 'toggle') {
      const active = config.options.filter((o) => value[o.value] !== false).map((o) => o.label);
      return active.join(', ') || null;
    }
    if (config.type === 'select' && config.options.length > 0) {
      const opt = config.options.find((o) => o.value === value);
      return opt?.label || value;
    }
    if (config.type === 'select') {
      return value;
    }
    if (config.type === 'selectMultipleCheckbox') {
      const checked = (value || []).filter((v) => v.checked);
      if (filterKey === 'department') {
        const checkedCities = (filterValues.city || []).filter((v) => v.checked);
        if (!checked.length && !checkedCities.length) return null;
        const parts = [];
        if (checked.length) parts.push(checked.map((v) => v.label).join(', '));
        if (checkedCities.length) parts.push(checkedCities.map((v) => v.label).join(', '));
        return parts.join(' · ');
      }
      if (!checked.length) return null;
      return checked.map((v) => v.label).join(', ');
    }
    if (config.type === 'dateRange') {
      if (!value || (!value.start && !value.end)) return null;
      const fmt = (d) => new Date(d).toLocaleDateString('fr-FR');
      if (value.start && value.end) return `${fmt(value.start)} → ${fmt(value.end)}`;
      if (value.start) return `Depuis ${fmt(value.start)}`;
      if (value.end) return `Jusqu'au ${fmt(value.end)}`;
      return null;
    }
    if (config.type === 'rating') {
      if (value.min && value.max) return `${value.min}-${value.max} étoiles`;
      if (value.min) return `Min ${value.min}`;
      if (value.max) return `Max ${value.max}`;
      return null;
    }
    return null;
  };

  // Sort filters: active first, then by original order
  const sortedFilterKeys = Object.keys(resolvedFilterConfigs).sort((a, b) => {
    const aActive = getFilterDisplayValue(a) !== null;
    const bActive = getFilterDisplayValue(b) !== null;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return 0;
  });

  const excludeAgency = () => {
    setInfoVivier({ listUsers: listAddVivier });
    toggle('exclude_agency');
  };

  const changeLocalisation = () => {
    setInfoVivier({ listUsers: listAddVivier });
    toggle('localisation_preferences');
  };

  useEffect(() => {
    if (usersVivier.length > 0) {
      for (let i = 0; i < usersVivier.length; i++) {
        for (let j = 0; j < result.length; j++) {
          if (usersVivier[i].id_user.toString() === result[j]._id.toString()) {
            result[j].viviers =
              usersVivier[i].viviers?.map((vivier) => vivier.name) || [];
          }
        }
      }
      setResult(result);
      setUsersVivier([]);
      setInfoVivier({ listUsers: [] });
      setAddVivier([]);
    }
  }, [usersVivier]);

  const searchRulesTooltip = (
    <Tooltip
      text={
        <div className="text-xs space-y-1">
          <p className="font-semibold">Règles de recherche :</p>
          <p>• <span className="font-mono">"mot"</span> : recherche exacte</p>
          <p>• <span className="font-semibold">ET</span> : les deux termes requis</p>
          <p>• <span className="font-semibold">OU</span> : au moins un terme</p>
          <p>• <span className="font-semibold">NON</span> : exclure le terme</p>
          <p>• <span className="font-mono">( )</span> : grouper les opérations</p>
          <p>• <span className="font-mono">mot*</span> : recherche par préfixe</p>
          <p>• <span className="font-mono">mot~N</span> : recherche approximative</p>
          <p>• <span className="font-mono">"phrase"~N</span> : recherche de proximité</p>
          <p className="mt-1 text-gray-300">Ex : ingénieur ET (logiciel OU matériel)</p>
        </div>
      }
      placement="top"
    >
      <div className="cursor-pointer">
        <InformationCircle wh={16} color="#6B7280" />
      </div>
    </Tooltip>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <SubNavbar>
        <div className="flex justify-between items-center w-full gap-3">
          <MediaQuery minWidth={768}>
            <div className="flex gap-3 items-center">
              <div className="w-[320px]">
                <StyledInput
                  type="text"
                  placeholder="Rechercher dans le CV.."
                  imgFront={<SearchLoop wh={16} color="#6B7280" />}
                  imgBack={searchRulesTooltip}
                  highlightContent={searchHighlight}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="w-[140px]">
                <select
                  className="w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-lg text-sm focus:border-yellow-500 focus:outline-none"
                  onChange={(e) => setOptionSearch(e.target.value)}
                  value={optionSearch}
                >
                  <option value="">Tous</option>
                  <option value="id_bestt">ID BeSTT</option>
                  <option value="name">Nom</option>
                  <option value="mail">Email</option>
                  <option value="phone">Téléphone</option>
                </select>
              </div>
            </div>
            <Button
              color={'SECONDARY'}
              size={'SM'}
              onClick={() => setIsFilterPanelOpen(true)}
            >
              <Filter wh={16} color="#374151" />
              <span>Plus de filtres</span>
              {activeFilterCount > 0 && (
                <Badge type="WARNING">{activeFilterCount}</Badge>
              )}
            </Button>
          </MediaQuery>
          <MediaQuery maxWidth={767}>
            <div className="flex gap-3 items-center flex-1">
              <div className="flex-1">
                <StyledInput
                  type="text"
                  placeholder="Rechercher..."
                  imgFront={<SearchLoop wh={16} color="#6B7280" />}
                  imgBack={searchRulesTooltip}
                  highlightContent={searchHighlight}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <Button
              color={'SECONDARY'}
              size={'SM'}
              onClick={openFilterSheet}
            >
              <Filter wh={16} color="#374151" />
              <span>Filtrer</span>
              {activeFilterCount > 0 && (
                <Badge type="WARNING">{activeFilterCount}</Badge>
              )}
            </Button>
          </MediaQuery>
        </div>
      </SubNavbar>
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div className="flex-1 overflow-auto">
          {searchCandidatWaiting && (
            <div className="p-3 tab:p-4 lg:p-8">
              <div
                className={`flex flex-col ${
                  width <= 460 ? 'gap-5' : 'gap-10'
                } justify-center items-center h-80`}
              >
                <div className={'animate-spin'}>
                  <Loading2 wh={width <= 460 ? 40 : 60} color={'#FF9600'} />
                </div>
              </div>
            </div>
          )}
          {!searchCandidatWaiting && (
            <div className="h-full">
              <TableVivier
                users={result}
                isWaiting={searchCandidatWaiting}
                setSortName={setSortName}
                sortName={sortName}
                setAddVivier={setAddVivier}
                list={listAddVivier}
              />
            </div>
          )}
        </div>
        {!searchCandidatWaiting &&
        currentPage &&
        totalPages >= 1 &&
        setCurrentPage ? (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-8 py-4">
            <Pagination
              currentPage={currentPage}
              countPerPage={countPerPage}
              countTotal={countTotal}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
            />
          </div>
        ) : null}

        {/* Fixed bottom bar for multiple selection actions */}
        {listAddVivier.length > 0 && (
          <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white p-1.5 rounded z-50">
            <div className="flex items-center text-sm h-10">
              <div className="flex gap-1 items-center py-2 px-3">
                <div className="w-fit">
                  <Badge type="WARNING">{listAddVivier.length}</Badge>
                </div>
                sélectionné{listAddVivier.length > 1 ? 's' : ''}
              </div>
              <div className="w-0.5 h-1/2 self-center bg-white" />
              <button
                onClick={() => handleAddVivier()}
                className="flex gap-1 items-center py-2 px-3 rounded hover:bg-gray-700"
              >
                <Plus wh={16} color="#FFF" />
                <span>Ajouter au vivier</span>
              </button>
              <div className="w-0.5 h-1/2 self-center bg-white" />
              <button
                onClick={() => changeLocalisation()}
                className="flex gap-1 items-center py-2 px-3 rounded hover:bg-gray-700"
              >
                <Localisation wh={16} color="#FFF" />
                <span>Localisation</span>
              </button>
              <div className="w-0.5 h-1/2 self-center bg-white" />
              <button
                onClick={() => excludeAgency()}
                className="flex gap-1 items-center py-2 px-3 rounded hover:bg-gray-700 text-red-400"
              >
                <BanIcon wh={16} color="#F87171" />
                <span>Exclure</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Panel latéral droit - Filtres desktop */}
      {isFilterPanelOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsFilterPanelOpen(false)}>
          <div className="absolute inset-0 bg-gray-900 opacity-50 transition-opacity duration-300" />
          <div
            className="absolute right-0 top-0 h-full w-full max-w-[520px] bg-white shadow-xl z-50 flex flex-col animate-slideR"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between p-5 gap-5">
              <h3 className="ft-lg text-[#202124] font-semibold my-auto flex-1 min-w-0 flex items-center gap-2">
                Filtres
                {activeFilterCount > 0 && (
                  <div className="bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px] ft-sm">
                    {activeFilterCount}
                  </div>
                )}
              </h3>
              <div className="flex items-center gap-2 my-auto">
                <button
                  type="button"
                  className="flex items-center justify-center border rounded p-1"
                  onClick={() => setIsFilterPanelOpen(false)}
                >
                  <CrossIcon wh={20} color="#111827" />
                </button>
              </div>
            </div>

            {/* Body - Filter list */}
            <div className="flex-1 overflow-y-auto px-6 py-4" ref={filterListRef}>
              <div className="flex flex-col gap-2">
                {sortedFilterKeys.map((filterKey, index) => {
                  const config = resolvedFilterConfigs[filterKey];
                  const isExpanded = expandedFilter === filterKey;
                  const displayValue = getFilterDisplayValue(filterKey);
                  const hasValue = displayValue !== null;
                  const isFirstInactive = index > 0 && !hasValue && getFilterDisplayValue(sortedFilterKeys[index - 1]) !== null;

                  if (isExpanded) {
                    return (
                      <div
                        key={filterKey}
                        className="border border-yellow-400 rounded-md overflow-hidden bg-white sh-sm"
                      >
                        {/* Expanded filter card header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-yellow-50">
                          <span className="ft-sm font-medium text-gray-900">
                            {config.label}
                          </span>
                          <button
                            onClick={() => setExpandedFilter(null)}
                            className="p-0.5 rounded hover:bg-yellow-100 transition-colors"
                          >
                            <CrossIcon wh={14} color="#374151" />
                          </button>
                        </div>

                        {/* Filter card body */}
                        <div className="px-4 py-3">
                          {/* Select type */}
                          {config.type === 'select' && (
                            <div className="flex flex-col gap-1">
                              {config.options.length > 0 ? (
                                config.options.map((opt) => (
                                  <button
                                    key={opt.value}
                                    onClick={() => {
                                      setFilterValue(filterKey, opt.value);
                                      setExpandedFilter(null);
                                    }}
                                    className={`text-left px-3 py-2 rounded-md ft-sm transition-colors ${
                                      filterValues[filterKey] === opt.value
                                        ? 'bg-yellow-50 text-yellow-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                ))
                              ) : (
                                <input
                                  type="text"
                                  placeholder={config.placeholder || 'Rechercher...'}
                                  value={filterValues[filterKey] || ''}
                                  onChange={(e) => setFilterValue(filterKey, e.target.value)}
                                className="w-full h-9 px-3 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
                              />
                            )}
                          </div>
                        )}

                          {/* SelectMultipleCheckbox type */}
                          {config.type === 'selectMultipleCheckbox' && (
                            <div className="flex flex-col gap-3">
                              <StyledInput
                                type="selectMultipleCheckbox"
                                placeholder={config.placeholder || 'Sélectionner...'}
                                list={
                                  filterValues[filterKey] ||
                                  config.options.map((v) => ({ ...v, checked: false }))
                                }
                                value={
                                  (filterValues[filterKey] || []).some((v) => v.checked)
                                    ? (filterValues[filterKey] || [])
                                        .filter((v) => v.checked)
                                        .map((v) => v.label)
                                        .join(', ')
                                    : config.placeholder || 'Sélectionner...'
                                }
                                onChange={(updatedList) =>
                                  setFilterValue(filterKey, updatedList)
                                }
                                isSearchable
                                showChevron
                                showCheck
                                dropdownFullWidth
                              />
                              {filterKey === 'department' && cityOptions.length > 0 && (
                                <StyledInput
                                  type="selectMultipleCheckbox"
                                  placeholder="Sélectionner une ville"
                                  list={
                                    filterValues.city ||
                                    cityOptions.map((v) => ({ ...v, checked: false }))
                                  }
                                  value={
                                    (filterValues.city || []).some((v) => v.checked)
                                      ? (filterValues.city || [])
                                          .filter((v) => v.checked)
                                          .map((v) => v.label)
                                          .join(', ')
                                      : 'Sélectionner une ville'
                                  }
                                  onChange={(updatedList) =>
                                    setFilterValue('city', updatedList)
                                  }
                                  isSearchable
                                  showChevron
                                  showCheck
                                  dropdownFullWidth
                                />
                              )}
                            </div>
                          )}

                          {/* Toggle type (OS, CV) */}
                          {config.type === 'toggle' && (
                            <div className="flex gap-2">
                              {config.options.map((opt) => {
                                const isExclusive = config.exclusive;
                                const isActive = isExclusive
                                  ? filterValues[filterKey] === opt.value
                                  : filterValues[filterKey]?.[opt.value] !== false;
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => {
                                      if (isExclusive) {
                                        const newValue = filterValues[filterKey] === opt.value ? null : opt.value;
                                        setFilterValue(filterKey, newValue);
                                        if (newValue !== null) setExpandedFilter(null);
                                      } else {
                                        setFilterValue(filterKey, {
                                          ...config.options.reduce(
                                            (acc, o) => ({
                                              ...acc,
                                              [o.value]: filterValues[filterKey]?.[o.value] !== false,
                                            }),
                                            {}
                                          ),
                                          [opt.value]: !isActive,
                                        });
                                      }
                                    }}
                                    className={`flex-1 py-2 px-3 rounded-md ft-sm font-medium transition-colors border ${
                                      isActive
                                        ? 'bg-yellow-50 border-yellow-400 text-yellow-700'
                                        : 'bg-white border-gray-200 text-gray-400'
                                    }`}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          )}


                          {/* DateRange type */}
                          {config.type === 'dateRange' && (
                            <div className="flex flex-col gap-3">
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-gray-500">À partir du</span>
                                <DatePicker
                                  selected={filterValues[filterKey]?.start ? new Date(filterValues[filterKey].start + 'T00:00:00') : null}
                                  onChange={(date) => {
                                    if (!date) return setFilterValue(filterKey, { ...filterValues[filterKey], start: null });
                                    const y = date.getFullYear();
                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                    const d = String(date.getDate()).padStart(2, '0');
                                    setFilterValue(filterKey, { ...filterValues[filterKey], start: `${y}-${m}-${d}` });
                                  }}
                                  dateFormat="dd/MM/yyyy"
                                  placeholderText="jj/mm/aaaa"
                                  isClearable
                                  maxDate={filterValues[filterKey]?.end ? new Date(filterValues[filterKey].end + 'T00:00:00') : null}
                                  className="w-full h-9 px-3 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
                                  locale="fr-FR"
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-gray-500">Jusqu'au</span>
                                <DatePicker
                                  selected={filterValues[filterKey]?.end ? new Date(filterValues[filterKey].end + 'T00:00:00') : null}
                                  onChange={(date) => {
                                    if (!date) return setFilterValue(filterKey, { ...filterValues[filterKey], end: null });
                                    const y = date.getFullYear();
                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                    const d = String(date.getDate()).padStart(2, '0');
                                    setFilterValue(filterKey, { ...filterValues[filterKey], end: `${y}-${m}-${d}` });
                                  }}
                                  dateFormat="dd/MM/yyyy"
                                  placeholderText="jj/mm/aaaa"
                                  isClearable
                                  minDate={filterValues[filterKey]?.start ? new Date(filterValues[filterKey].start + 'T00:00:00') : null}
                                  className="w-full h-9 px-3 ft-sm border border-gray-200 rounded-md focus:border-yellow-500 focus:outline-none"
                                  locale="fr-FR"
                                />
                              </div>
                            </div>
                          )}

                          {/* Rating type */}
                          {config.type === 'rating' && (
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500 w-6">Min</span>
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                      key={star}
                                      onClick={() => {
                                        const currentMax = filterValues[filterKey]?.max;
                                        setFilterValue(filterKey, {
                                          ...filterValues[filterKey],
                                          min: star,
                                          ...(currentMax !== undefined && star > currentMax ? { max: star } : {}),
                                        });
                                      }}
                                      className="p-0.5 transition-transform hover:scale-110"
                                    >
                                      <Star
                                        wh={20}
                                        color={
                                          star <= (filterValues[filterKey]?.min || 0)
                                            ? '#F59E0B'
                                            : '#D1D5DB'
                                        }
                                      />
                                    </button>
                                  ))}
                                </div>
                                {filterValues[filterKey]?.min && (
                                  <span className="text-xs text-gray-500">
                                    {filterValues[filterKey].min}/5
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500 w-6">Max</span>
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                      key={star}
                                      onClick={() => {
                                        const currentMin = filterValues[filterKey]?.min;
                                        setFilterValue(filterKey, {
                                          ...filterValues[filterKey],
                                          max: star,
                                          ...(currentMin !== undefined && star < currentMin ? { min: star } : {}),
                                        });
                                      }}
                                      className="p-0.5 transition-transform hover:scale-110"
                                    >
                                      <Star
                                        wh={20}
                                        color={
                                          star <= (filterValues[filterKey]?.max || 0)
                                            ? '#F59E0B'
                                            : '#D1D5DB'
                                        }
                                      />
                                    </button>
                                  ))}
                                </div>
                                {filterValues[filterKey]?.max && (
                                  <span className="text-xs text-gray-500">
                                    {filterValues[filterKey].max}/5
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    );
                  }

                  // Divider between active and inactive filters
                  if (isFirstInactive) {
                    return (
                      <div key={`divider-${filterKey}`} className="my-2 border-t border-gray-200" />
                    );
                  }

                  // Has value but not expanded - show compact card
                  if (hasValue) {
                    return (
                      <div
                        key={filterKey}
                        className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer hover:bg-yellow-100 transition-colors"
                        onClick={() => setExpandedFilter(filterKey)}
                      >
                        <div className="flex flex-col">
                          <span className="ft-sm font-medium text-gray-900">
                            {config.label}
                          </span>
                          <span className="text-xs text-yellow-700">{displayValue}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            clearFilterValue(filterKey);
                          }}
                          className="p-1 rounded hover:bg-yellow-200 transition-colors"
                        >
                          <CrossIcon wh={14} color="#374151" />
                        </button>
                      </div>
                    );
                  }

                  // No value - show simple row
                  return (
                    <button
                      key={filterKey}
                      onClick={() => setExpandedFilter(filterKey)}
                      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                    >
                      <span className="ft-sm text-gray-600">
                        {config.label}
                      </span>
                      <Plus wh={16} color="#9CA3AF" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button
                color={'SECONDARY'}
                size={'SM'}
                className="flex-1"
                onClick={resetAllFilters}
              >
                Réinitialiser
              </Button>
              <Button
                color={'PRIMARY'}
                size={'SM'}
                className="flex-1"
                onClick={applyAllFilters}
              >
                Appliquer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Sheet Filtre pour mobile */}
      <BottomSheet
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title="Filtrer"
      >
        <div className="flex flex-col gap-4">
          {/* Recherche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <StyledInput
              type="text"
              placeholder="Rechercher..."
              imgFront={<SearchLoop wh={16} color="#6B7280" />}
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
            />
          </div>

          {/* Type de recherche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher par
            </label>
            <select
              className="w-full h-10 pl-3 pr-8 ft-b placeholder-gray-600 border rounded-lg text-sm focus:border-yellow-500 focus:outline-none"
              onChange={(e) => setTempOptionSearch(e.target.value)}
              value={tempOptionSearch}
            >
              <option value="">Tous</option>
              <option value="id_bestt">ID BeSTT</option>
              <option value="name">Nom</option>
              <option value="mail">Email</option>
              <option value="phone">Téléphone</option>
            </select>
          </div>

          {/* Bouton Appliquer */}
          <div className="mt-2">
            <Button
              color={'PRIMARY'}
              size={'FULL'}
              onClick={applyFilters}
            >
              Appliquer
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

export default BodyVivier;
